'use client';

import {
  ProfileButtonShape,
  ProfileHeaderStyle,
  ProfileLayout,
  ProfileTheme,
} from '@up2stay/up2promo-api-client';
import { Label } from '@up2stay/up2promo-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';

import { LogoUpload } from '@/core/components/settings/logo-upload';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import type { ProfileStylingFormSlice } from '@/core/lib/profile-styling-form';

export type ProfileAppearanceDict = {
  layout: string;
  layoutDescription: string;
  layoutCards: string;
  layoutCentered: string;
  layoutClassic: string;
  theme: string;
  themeDescription: string;
  themeAuto: string;
  themeDark: string;
  themeLight: string;
  headerStyle: string;
  headerStyleDescription: string;
  headerStyleGradient: string;
  headerStyleImage: string;
  headerStyleMinimal: string;
  headerImage: string;
  headerImageDescription: string;
  buttonShape: string;
  buttonShapeDescription: string;
  buttonShapeRounded: string;
  buttonShapePill: string;
  buttonShapeSquare: string;
  buttonShapeDefault: string;
};

type ProfileAppearanceFieldsProps = {
  dict: ProfileAppearanceDict;
  values: Pick<
    ProfileStylingFormSlice,
    'layout' | 'theme' | 'headerStyle' | 'headerImage' | 'buttonShape'
  >;
  disabled?: boolean;
  onChange: <
    K extends
      | 'layout'
      | 'theme'
      | 'headerStyle'
      | 'headerImage'
      | 'buttonShape',
  >(
    key: K,
    value: ProfileStylingFormSlice[K],
  ) => void;
};

export function ProfileAppearanceFields({
  dict,
  values,
  disabled = false,
  onChange,
}: ProfileAppearanceFieldsProps) {
  const rawHeaderPath = values.headerImage?.path?.trim();
  const headerPreview = rawHeaderPath
    ? rawHeaderPath.startsWith('data:') ||
      rawHeaderPath.startsWith('blob:') ||
      rawHeaderPath.startsWith('http://') ||
      rawHeaderPath.startsWith('https://')
      ? rawHeaderPath
      : getAbsoluteFileUrl(rawHeaderPath)
    : undefined;

  return (
    <SettingsGroup>
      <SettingsGroupRows>
        <SettingsGroupRow>
          <div className="space-y-0.5 min-w-0">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="profile-layout">{dict.layout}</Label>
              <SettingsInfoHint label={dict.layout}>
                {dict.layoutDescription}
              </SettingsInfoHint>
            </div>
            <Select
              value={values.layout}
              disabled={disabled}
              onValueChange={(value) =>
                onChange('layout', value as ProfileLayout)
              }
            >
              <SelectTrigger id="profile-layout" className="min-w-0 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ProfileLayout.CARDS}>
                  {dict.layoutCards}
                </SelectItem>
                <SelectItem value={ProfileLayout.CENTERED}>
                  {dict.layoutCentered}
                </SelectItem>
                <SelectItem value={ProfileLayout.CLASSIC}>
                  {dict.layoutClassic}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </SettingsGroupRow>

        <SettingsGroupRow separated>
          <div className="space-y-0.5 min-w-0">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="profile-theme">{dict.theme}</Label>
              <SettingsInfoHint label={dict.theme}>
                {dict.themeDescription}
              </SettingsInfoHint>
            </div>
            <Select
              value={values.theme}
              disabled={disabled}
              onValueChange={(value) =>
                onChange('theme', value as ProfileTheme)
              }
            >
              <SelectTrigger id="profile-theme" className="min-w-0 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ProfileTheme.AUTO}>
                  {dict.themeAuto}
                </SelectItem>
                <SelectItem value={ProfileTheme.DARK}>
                  {dict.themeDark}
                </SelectItem>
                <SelectItem value={ProfileTheme.LIGHT}>
                  {dict.themeLight}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </SettingsGroupRow>

        <SettingsGroupRow separated>
          <div className="space-y-0.5 min-w-0">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="profile-header-style">{dict.headerStyle}</Label>
              <SettingsInfoHint label={dict.headerStyle}>
                {dict.headerStyleDescription}
              </SettingsInfoHint>
            </div>
            <Select
              value={values.headerStyle}
              disabled={disabled}
              onValueChange={(value) =>
                onChange('headerStyle', value as ProfileHeaderStyle)
              }
            >
              <SelectTrigger
                id="profile-header-style"
                className="min-w-0 w-full"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ProfileHeaderStyle.GRADIENT}>
                  {dict.headerStyleGradient}
                </SelectItem>
                <SelectItem value={ProfileHeaderStyle.IMAGE}>
                  {dict.headerStyleImage}
                </SelectItem>
                <SelectItem value={ProfileHeaderStyle.MINIMAL}>
                  {dict.headerStyleMinimal}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </SettingsGroupRow>

        {values.headerStyle === ProfileHeaderStyle.IMAGE ? (
          <SettingsGroupRow separated>
            <LogoUpload
              label={dict.headerImage}
              description={dict.headerImageDescription}
              currentPreview={headerPreview}
              aspect={3}
              onUpload={(fileId, preview) => {
                onChange('headerImage', { id: fileId, path: preview });
              }}
              onRemove={() => onChange('headerImage', null)}
            />
          </SettingsGroupRow>
        ) : null}

        <SettingsGroupRow separated>
          <div className="space-y-0.5 min-w-0">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="profile-button-shape">{dict.buttonShape}</Label>
              <SettingsInfoHint label={dict.buttonShape}>
                {dict.buttonShapeDescription}
              </SettingsInfoHint>
            </div>
            <Select
              value={values.buttonShape ?? 'default'}
              disabled={disabled}
              onValueChange={(value) =>
                onChange(
                  'buttonShape',
                  value === 'default' ? null : (value as ProfileButtonShape),
                )
              }
            >
              <SelectTrigger
                id="profile-button-shape"
                className="min-w-0 w-full"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  {dict.buttonShapeDefault}
                </SelectItem>
                <SelectItem value={ProfileButtonShape.ROUNDED}>
                  {dict.buttonShapeRounded}
                </SelectItem>
                <SelectItem value={ProfileButtonShape.PILL}>
                  {dict.buttonShapePill}
                </SelectItem>
                <SelectItem value={ProfileButtonShape.SQUARE}>
                  {dict.buttonShapeSquare}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </SettingsGroupRow>
      </SettingsGroupRows>
    </SettingsGroup>
  );
}
